import {Department} from '@app/core/models/department.model';
import {Group} from '@app/core/models/group.model';
import {FileEntry} from '@app/core/models/fileEntry.model';
import {RDate} from '@app/core/models/rDate.model';
import {Shift} from '@app/core/models/shift.model';
import {PermanentlyEmployedSettings} from '@app/core/models/permanentlyEmployedSettings.model';
import {InternationalizationService} from '@app/core/services/internationalization.service';
import {BaseModel} from '@app/core/models/base.model';
import {IBackendLanguage} from '@app/core/models/tenant.model';
import {IQODropDownAction} from '@quickorder/angular-web-lib/lib/types/interfaces/q-o-drop-down-action.interface';
import * as moment from 'moment';
import {Moment} from 'moment';

interface IHourSalary {
	group_id: number;
	hour_salary: number;
}

export interface IProfilePicture {
	download_url: string;
	extension: string;
	file_name: string;
	id: number;
	mime: string;
	original_file_name: string;
	public: boolean;
	size: number;
}

export class User extends BaseModel {
	public id: number;
	public first_name: string;
	public last_name: string;
	public email: string;
	public birth_date: string;
	public permission_level: number;
	public phone_number: number;
	public phone_ext: string;
	public groups: Group[] = [];
	public departments: Department[] = [];
	public available_departments: Department[] = [];
	public file_entries: FileEntry[] = [];
	public getting_started: boolean = false;
	public shift: Shift;
	public danlon_payment_number: string;
	public permanently_employed: boolean;
	public permanently_employed_settings: PermanentlyEmployedSettings;
	public danlon_payment_numbers: { department_id: number, payment_number: string }[];
	public selected_department_id: number;
	public language: IBackendLanguage;
	public hour_salaries: IHourSalary[];
	public integration_id: number;
	public created_at: RDate;
	public updated_at: RDate;
	public deleted_at: RDate;
	public deactivated_at: RDate;
	public profile_picture: IProfilePicture;
	public new_user: boolean;
	public actions: IQODropDownAction<User>[];
	public send_mail: boolean;
	public current_password: string;
	public new_password: string;
	public pin_code_is_set: boolean;
	public pin_code: string;

	public social_security_number: string;
	public address: string;
	public city: string;
	public postal_code: string;
	public account_number: string;
	public seniority: number;
	public employed_at: Moment;
	public terminated_at: Moment;
	public termination_note: string;
	public note: string;
	public social_security_number_is_set?: boolean;

	constructor(tenantTZ: string, interService: InternationalizationService) {
		super(tenantTZ, interService);
	}

	public getName(): string {
		return this.getFullName();
	}

	public isAdmin(): boolean {
		return (this.permission_level === 2);
	}

	public getPaymentNumber(): string {
		return 'danlon_payment_number';
	}

	public getFullName(): string {
		return this.first_name + ' ' + this.last_name;
	}

	public getProfilePictureUrl(): string {
		return this.profile_picture?.download_url;
	}

	public getPhoneNumber(): string {
		if (this.phone_ext && this.phone_number) {
			return this.phone_ext + ' ' + this.phone_number;
		} else if (this.phone_number) {
			return String(this.phone_number);
		} else {
			return '';
		}
	}

	public getInitials(): string {
		if (!this.first_name || !this.last_name) {

			return '';
		}

		return (this.first_name.slice(0, 1) + this.last_name.slice(0, 1)).toUpperCase();
	}

	public getShortName(): string {
		if (this.id !== 1) {

			const lastNames = this.last_name.split(' ');
			const firstNames = this.first_name.split(' ');
			const firstName = firstNames[0];
			const lastName = lastNames[lastNames.length - 1];

			return firstName + ' ' + lastName;
		} else {
			return this.first_name + ' ' + this.last_name;
		}
	}

	public deserialize(input: any): this {
		Object.assign(this, input);
		if (input.id === 1) {
			this.first_name = this.interService.translate.instant('USER.AVAILABLE');
			this.last_name = this.interService.translate.instant('USER.SHIFT');
		}
		if (input.departments) {
			this.departments = [];
			this.departments = input.departments.map(departmentInput => new Department(this.tenantTZ, this.interService).deserialize(departmentInput));
		}
		if (input.available_departments) {
			this.available_departments = [];
			this.available_departments = input.available_departments.map(departmentInput => new Department(this.tenantTZ, this.interService).deserialize(departmentInput));
		}
		if (input.groups) {
			this.groups = [];
			this.groups = input.groups.map(groupInput => new Group(this.tenantTZ, this.interService).deserialize(groupInput));
		}
		if (input.file_entries) {
			this.file_entries = [];
			this.file_entries = input.file_entries.map(fileEntryInput => new FileEntry(this.tenantTZ, this.interService).deserialize(fileEntryInput));
		}
		if (input.shift) {
			this.shift = new Shift(this.tenantTZ, this.interService).deserialize(input.shift);
		}
		if (input.permanently_employed_settings) {
			this.permanently_employed_settings = new PermanentlyEmployedSettings(this.tenantTZ, this.interService).deserialize(input.permanently_employed_settings);
		}
		if (input.created_at) {
			this.created_at = new RDate(this.tenantTZ, this.interService).deserialize(input.created_at);
		}
		if (input.updated_at) {
			this.updated_at = new RDate(this.tenantTZ, this.interService).deserialize(input.updated_at);
		}
		if (input.deactivated_at) {
			this.deactivated_at = new RDate(this.tenantTZ, this.interService).deserialize(input.deactivated_at);
		}
		if (input.hour_salaries) {
			this.hour_salaries = input.hour_salaries.map(hs => ({
				...hs,
				hour_salary: Number(hs.hour_salary)
			}));
		}
		if (input.seniority) {
			this.seniority = parseFloat(input.seniority);
		}
		if (input.employed_at) {
			this.employed_at = moment(input.employed_at);
		}
		if (input.terminated_at) {
			this.terminated_at = moment(input.terminated_at);
		}

		return this;
	}

	public serialize(): any {
		return JSON.stringify(this, (key, value) => {
			if (!value) {
				return value;
			}

			switch (key) {
				case 'interService':
					return;
				case 'created_at':
					return (value as RDate).databaseDate;
				case 'updated_at':
					return (value as RDate).databaseDate;
				case 'deactivated_at':
					return (value as RDate).databaseDate;
				case 'hour_salaries':
					return (value as IHourSalary[]).map(hs => ({
						...hs,
						hour_salary: hs.hour_salary + '',
					}));
				case 'seniority':
					return value + '';
			}

			return value;
		});
	}
}
